/**
 * ByDeviceCalendar — Block (PROD-2007)
 *
 * Time-row × DEVICE-column grid: the calendar's Devices tab. Same day grid as
 * By-Doctor, one column per device of the selected practice. A device is a
 * machine (HBOT chamber, IV chair, cryo…) with its own schedule and products;
 * nothing on this grid references a practitioner.
 *
 * Rulings (Andrew, 12 Sep 2026):
 *   - column per device, ordered by subtype then name (the caller orders);
 *   - header line 2 = room · technician — the subtype half and the "mobile"
 *     no-room fallback both left on 2026-09-14 (see `deviceHeaderLine`);
 *   - presence shading from `ResourceAvailability`; `[]` = no shading and a
 *     "Not bookable" marker on the header;
 *   - a booking shows the patient and the PRODUCT name — no doctor;
 *   - blocks beat everything (rendered above bookings and shading);
 *   - colours come from the product (appointment profile colour).
 *
 * Data contract:
 *   - `devices`               column set, already filtered (subtype) + ordered.
 *   - `rowsByDevice`          `deviceId -> DayListRow[]` (`groupRowsByDevice`).
 *   - `availabilityByDevice`  `deviceId -> periods | undefined` — `undefined`
 *                             while unknown (no marker, no shading), `[]` =
 *                             not bookable.
 *   - `hours`                 hour gutter; caller extends it over the windows.
 */

import { useEffect, useMemo, useRef } from 'react';
import { categoryColor } from '@/shared/lib/category-color';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Ban, CalendarPlus, Repeat } from 'lucide-react';
import { TimeLabel } from '@/components/atoms';
import { StatusBadge, type AppointmentStatusType } from '@/components/atoms';
import { BLOCK_STRIPES } from '@/components/cards/appointment-tile';
import { cn } from '@/lib/utils';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import type { CalendarDevice, ResourceAvailabilityPeriod } from '@/types/scheduling-api';
import { computeOverlapLayout, slotToPercents } from '@/scheduling/lib/overlap-layout';
import { quickCreateStartISO, dragDeltaToNewStartISO } from '@/scheduling/lib/quick-create-slot';
import { deviceHeaderLine } from '@/scheduling/lib/device-columns';
import {
  isNotBookable,
  parseAvailability,
  windowToGridGeometry,
  windowsForDay,
} from '@/scheduling/lib/device-availability';
import { CurrentTimeLine } from '@/components/blocks/current-time-line';
import { GutterClock } from '@/components/blocks/gutter-clock';

const HOUR_HEIGHT = 60; // px per hour row — same density as By-Doctor.

/** Minute-of-day range for a DayListRow, for overlap layout (PROD-982). */
function rowMinutes(row: DayListRow): { startMin: number; endMin: number } {
  const s = new Date(row.startISO);
  const e = new Date(row.endISO);
  return {
    startMin: s.getHours() * 60 + s.getMinutes(),
    endMin: e.getHours() * 60 + e.getMinutes(),
  };
}

interface ByDeviceCalendarProps {
  devices: CalendarDevice[];
  rowsByDevice: Map<string, DayListRow[]>;
  availabilityByDevice: Map<string, ResourceAvailabilityPeriod[] | undefined>;
  hours?: number[];
  selectedDate: Date;
  /** True while the device list is still loading (renders a quiet placeholder). */
  isLoading?: boolean;
  /** Copy for the empty state when a subtype filter hides every device. */
  filtered?: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
  /**
   * "Book a device" from a column header (no start) or from an empty cell
   * double-click (`startISO` = local no-Z start under the pointer). The
   * consumer opens the Book-a-device flow with the device preselected.
   */
  onBookDevice?: (deviceId: string, startISO?: string) => void;
  /**
   * Drag-to-reschedule — the existing reschedule path (time-only, the device
   * never changes). `(id, newStartISO)`; the consumer confirms + fires
   * `useRescheduleAppointment`. Absent → tiles are click-only.
   */
  onAppointmentDrop?: (payload: { id: string; newStartISO: string }) => void;
}

export function ByDeviceCalendar({
  devices,
  rowsByDevice,
  availabilityByDevice,
  hours = defaultHours(),
  selectedDate,
  isLoading = false,
  filtered = false,
  onAppointmentClick,
  onBookDevice,
  onAppointmentDrop,
}: ByDeviceCalendarProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const rowById = useMemo(() => {
    const m = new Map<string, DayListRow>();
    for (const rows of rowsByDevice.values()) {
      for (const r of rows) m.set(r.id, r);
    }
    return m;
  }, [rowsByDevice]);

  // Parsed + day-clipped windows per device, memoised so the shading layer
  // doesn't re-parse 57 responses on every pointer move.
  const windowsByDevice = useMemo(() => {
    const m = new Map<string, ReturnType<typeof windowsForDay>>();
    for (const d of devices) {
      const periods = availabilityByDevice.get(d.id);
      if (!periods) continue;
      m.set(d.id, windowsForDay(parseAvailability(periods), selectedDate));
    }
    return m;
  }, [devices, availabilityByDevice, selectedDate]);

  const isShowingToday = selectedDate.toDateString() === new Date().toDateString();

  // Open on the working day, not on midnight. The practitioner day list has
  // scrolled the current hour into view since PROD-1774; this grid never did,
  // so a Devices tab opened at 09:00 showed eight empty hours and the user had
  // to scroll to find the day's bookings (Andrew, 2026-09-14).
  //
  // Deliberately the same shape as `day-list-calendar`'s effect rather than a
  // second invention:
  //   - keyed on (date | hour | rowCount), not a boolean latch, so the scroll
  //     re-issues once the rows land and the layout settles — but never twice
  //     for the same state, so it cannot fight the user's own scrolling;
  //   - `scrollIntoView`: the browser walks the scroll-container chain and
  //     moves whichever one needs to move (this body since 15 Oct; the
  //     frame's pane before), which is exactly what #1018 relies on too;
  //   - one frame's delay so the commit has laid out before we measure.
  //
  // Only when the grid is showing TODAY: on any other date there is no
  // "current hour" to centre on, and jumping the user to an arbitrary row
  // would be worse than starting at the top.
  const gridRef = useRef<HTMLDivElement>(null);
  const scrolledForRef = useRef<string | null>(null);
  const currentHour = isShowingToday ? new Date().getHours() : undefined;
  const rowCount = useMemo(
    () => devices.reduce((n, d) => n + (rowsByDevice.get(d.id)?.length ?? 0), 0),
    [devices, rowsByDevice],
  );

  useEffect(() => {
    if (currentHour === undefined || hours.length === 0) return;

    // Clamp into the rendered range: at 02:00 the grid may start at 08:00, and
    // at 23:00 it may end at 17:00 — scroll to the nearest rendered hour rather
    // than giving up.
    const target = Math.min(Math.max(currentHour, hours[0]), hours[hours.length - 1]);
    const key = `${selectedDate.toDateString()}|${target}|${rowCount}`;
    if (scrolledForRef.current === key) return;

    const id = window.setTimeout(() => {
      const el = gridRef.current?.querySelector<HTMLElement>(`[data-hour="${target}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
      scrolledForRef.current = key;
    }, 50);
    return () => window.clearTimeout(id);
  }, [currentHour, hours, rowCount, selectedDate]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onAppointmentDrop) return;
    const id = String(event.active.id);
    const row = rowById.get(id);
    if (!row?.startISO) return;
    const newStartISO = dragDeltaToNewStartISO(row.startISO, event.delta.y, HOUR_HEIGHT);
    if (!newStartISO) return;
    if (new Date(newStartISO).getTime() < Date.now()) return;
    onAppointmentDrop({ id, newStartISO });
  };

  if (isLoading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-text-muted" aria-busy="true">
        Loading devices…
      </div>
    );
  }
  if (devices.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-text-muted">
        {/* The caller narrows to BOOKABLE devices since 2026-09-13, so the
            unfiltered empty case is far more often "57 machines, none
            configured" than "no machines" — say what is missing rather than
            sending the user to add a device they already have. */}
        {filtered
          ? 'No devices match this subtype filter.'
          : 'No bookable devices at this practice — a device needs working hours and at least one product under Settings › Devices › Schedule.'}
      </div>
    );
  }

  const startHour = hours[0] ?? 8;
  const endHour = (hours[hours.length - 1] ?? 17) + 1;
  const dateLabel = selectedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  // Nothing hook-shaped belongs below this line: the two early returns above
  // (loading, and no bookable devices) mean any hook here runs on some renders
  // and not others. That is exactly what React error #310 reports, and it took
  // the whole Devices tab down to the route error boundary — see the auto-scroll
  // hooks lifted to the top of this component.


  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex flex-col flex-1 min-h-0 bg-surface-card">
        <div className="px-4 py-3 border-b border-border-default">
          <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
            Devices · {dateLabel}
          </p>
        </div>

        {/* The one scroller, both axes (Antonin, 15 Oct: "solidly fixed"). Until 15 Oct this wrapper deliberately did
            NOT scroll: FrameScheduling's pane was a block box that scrolled the strip and the grid together, so an inner
            `overflow-auto` here grew to its content (1490 px inside a 589 px pane) and never moved, and the header
            pinned to the pane instead (Andrew, 14 Sep: "can we make this line fixed while scrolling down?"). The pane
            now stays still on the Calendar tab and bounds this body, so the header pins here (top) and so does the
            time gutter (left), with the device columns scrolling under both (showcase-bridge `[data-cal-*]`). */}
        <div data-cal-scroll className="scrollbar-hide">
         <div data-cal-sheet>
          {/* Device column header strip — pinned to the top of the body above; the timezone/clock gutter cell is
              the row's first flex child and pins left as well (the corner). */}
          <div
            data-testid="device-header-row"
            data-cal-head
            className="flex border-b border-border-default"
          >
            <GutterClock data-cal-gutter className="w-16 flex-shrink-0 border-r border-border-subtle px-2 py-2" />
            {devices.map((d) => {
              const notBookable = isNotBookable(availabilityByDevice.get(d.id));
              return (
                <div
                  key={d.id}
                  data-testid={`device-column-header-${d.id}`}
                  className="flex-1 min-w-[160px] border-r border-border-subtle last:border-r-0 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate" title={d.name}>
                        {d.name}
                      </p>
                      <p className="text-xs text-text-muted truncate">{deviceHeaderLine(d)}</p>
                      {notBookable ? (
                        <span
                          className="mt-1 inline-flex items-center rounded-full bg-grey-100 px-2 py-px text-size-xs font-medium text-text-muted"
                          title="No working hours — set them under Settings › Devices › Schedule"
                        >
                          Not bookable
                        </span>
                      ) : null}
                    </div>
                    {onBookDevice && !notBookable ? (
                      <button
                        type="button"
                        onClick={() => onBookDevice(d.id)}
                        aria-label={`Book ${d.name}`}
                        className="flex-shrink-0 rounded-md p-1 text-brand-primary hover:bg-blue-100"
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time grid */}
          <div className="relative" ref={gridRef}>
            {hours.map((hour) => (
              <div
                key={hour}
                data-hour={hour}
                className="flex border-b border-border-subtle"
                // 24px of breathing room so the row does not sit flush under
                // the sticky header once scrollIntoView lands it.
                style={{ height: HOUR_HEIGHT, scrollMarginTop: 24 }}
              >
                <div data-cal-gutter className="w-16 flex-shrink-0 px-2 pt-1 border-r border-border-subtle">
                  <TimeLabel hour={hour} />
                </div>
                {devices.map((d) => {
                  const bookable = onBookDevice && !isNotBookable(availabilityByDevice.get(d.id));
                  return (
                    <div
                      key={d.id}
                      className={cn(
                        'flex-1 min-w-[160px] border-r border-border-subtle last:border-r-0',
                        bookable ? 'cursor-pointer hover:bg-blue-100/20' : '',
                      )}
                      onDoubleClick={
                        bookable
                          ? (e) => {
                              if (e.target !== e.currentTarget) return;
                              const rect = e.currentTarget.getBoundingClientRect();
                              const fraction =
                                rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0;
                              onBookDevice(d.id, quickCreateStartISO(selectedDate, hour, fraction));
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            ))}

            {/* Presence shading — the device's own hours, under the tiles.
                No shading for a device whose availability is unknown or []. */}
            <div
              className="absolute inset-0 pl-16 grid pointer-events-none"
              style={{ gridTemplateColumns: `repeat(${devices.length}, minmax(160px, 1fr))` }}
              aria-hidden="true"
            >
              {devices.map((d) => (
                <div key={d.id} className="relative">
                  {(windowsByDevice.get(d.id) ?? []).map((w, i) => {
                    const geo = windowToGridGeometry(w, startHour, endHour, HOUR_HEIGHT);
                    if (!geo) return null;
                    return (
                      <div
                        key={i}
                        data-testid={`device-presence-${d.id}`}
                        className="absolute left-0 right-0 bg-blue-100/40"
                        style={{ top: geo.top, height: geo.height }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Booking tiles — above the shading; blocks above bookings. */}
            <div
              className="absolute inset-0 pl-16 grid pointer-events-none"
              style={{ gridTemplateColumns: `repeat(${devices.length}, minmax(160px, 1fr))` }}
            >
              {devices.map((d) => {
                const rows = rowsByDevice.get(d.id) ?? [];
                const items = rows.map((row) => ({ row, ...rowMinutes(row) }));
                const layout = computeOverlapLayout(items);
                return (
                  <div key={d.id} className="relative" data-testid={`device-column-${d.id}`}>
                    {items.map((it) => {
                      const slot = layout.get(it);
                      const { leftPct, widthPct } =
                        slot && slot.columnCount > 1
                          ? slotToPercents(slot)
                          : { leftPct: undefined, widthPct: undefined };
                      return (
                        <DraggableDeviceTile
                          key={it.row.id}
                          row={it.row}
                          startHour={startHour}
                          endHour={endHour}
                          leftPct={leftPct}
                          widthPct={widthPct}
                          onClick={onAppointmentClick}
                          draggable={!!onAppointmentDrop && !it.row.isBlock}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {isShowingToday ? (
              <CurrentTimeLine
                startHour={startHour}
                endHour={endHour}
                hourHeight={HOUR_HEIGHT}
                className="left-16 right-0"
              />
            ) : null}
          </div>
         </div>
        </div>
      </div>
    </DndContext>
  );
}

interface DeviceTileProps {
  row: DayListRow;
  startHour: number;
  endHour: number;
  leftPct?: number;
  widthPct?: number;
  onClick?: (id: string) => void;
  dragRef?: (el: HTMLElement | null) => void;
  dragListeners?: ReturnType<typeof useDraggable>['listeners'];
  dragAttributes?: ReturnType<typeof useDraggable>['attributes'];
  dragTransform?: { x: number; y: number } | null;
}

function DraggableDeviceTile({ draggable, ...rest }: DeviceTileProps & { draggable: boolean }) {
  const { setNodeRef, listeners, attributes, transform } = useDraggable({
    id: rest.row.id,
    disabled: !draggable,
  });
  return (
    <DeviceTile
      {...rest}
      dragRef={draggable ? setNodeRef : undefined}
      dragListeners={draggable ? listeners : undefined}
      dragAttributes={draggable ? attributes : undefined}
      dragTransform={transform}
    />
  );
}

function DeviceTile({
  row,
  startHour,
  endHour,
  leftPct,
  widthPct,
  onClick,
  dragRef,
  dragListeners,
  dragAttributes,
  dragTransform,
}: DeviceTileProps) {
  const dragStyle = dragTransform
    ? { transform: `translate3d(0px, ${dragTransform.y}px, 0)`, opacity: 0.5, zIndex: 30 }
    : undefined;
  const isOverlap = leftPct != null && widthPct != null;
  const posClass = isOverlap ? 'absolute' : 'absolute left-1 right-1';
  const overlapStyle = isOverlap
    ? { left: `${leftPct}%`, width: `calc(${widthPct}% - 2px)` }
    : null;
  const start = new Date(row.startISO);
  const end = new Date(row.endISO);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const gridStartMinutes = startHour * 60;
  const gridEndMinutes = endHour * 60;
  const top =
    ((Math.max(startMinutes, gridStartMinutes) - gridStartMinutes) / 60) * HOUR_HEIGHT;
  const height = Math.max(
    ((Math.min(endMinutes, gridEndMinutes) - Math.max(startMinutes, gridStartMinutes)) / 60) *
      HOUR_HEIGHT,
    28,
  );
  if (endMinutes <= gridStartMinutes || startMinutes >= gridEndMinutes) return null;

  // Blocks beat everything: striped, on top (z-20), never a patient layout.
  if (row.isBlock) {
    const label = row.note?.trim() || 'Blocked';
    return (
      <button
        type="button"
        ref={dragRef}
        onClick={() => onClick?.(row.id)}
        className={cn(
          posClass,
          'z-20 rounded-md border border-border-default text-left px-2 py-1.5 shadow-sm hover:shadow-md transition-shadow pointer-events-auto overflow-hidden flex flex-col gap-1',
          BLOCK_STRIPES,
        )}
        style={{ top, height, ...overlapStyle, ...dragStyle }}
        // PHI rule (PROD-794): the block reason is free text — keep it out of `title`.
        title={`Blocked — ${row.timeRange}`}
        {...dragListeners}
        {...dragAttributes}
      >
        <div className="flex items-center gap-1 min-w-0">
          {row.isRecurring ? (
            <Repeat className="h-3 w-3 text-text-muted shrink-0" aria-label="Recurring block" />
          ) : (
            <Ban className="h-3 w-3 text-text-muted shrink-0" />
          )}
          <span className="text-xs font-semibold text-text-secondary truncate">{label}</span>
        </div>
        <span className="text-xs text-text-muted truncate">{row.timeRange}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      ref={dragRef}
      onClick={() => onClick?.(row.id)}
      data-testid={`device-booking-${row.id}`}
      className={cn(
        posClass,
        'z-10 rounded-md border bg-surface-card text-left pl-3 pr-2 py-1.5 shadow-sm hover:shadow-md transition-shadow pointer-events-auto overflow-hidden flex flex-col gap-1',
        dragRef ? 'cursor-grab active:cursor-grabbing touch-none' : '',
      )}
      // 4 px off the hour line, ending 4 px early (Antonin, 13 Oct: "unstick the cards from the top of the cell").
      style={{
        top: top + 4,
        height: Math.max(height - 8, 20),
        ...overlapStyle,
        ...dragStyle,
      }}
      // PHI-free title — time + status only.
      title={`${row.timeRange} — ${row.statusDisplay}`}
      {...dragListeners}
      {...dragAttributes}
    >
      {/* The category edge is the card's whole left shoulder, a 4 px colour bar clipped by the card's corners, not a left border bent by them (Antonin, 13 Oct). Colour
          from the PRODUCT (appointment profile colour): all HBOT 60s look the same across chambers. */}
      <span aria-hidden data-event-edge className="absolute inset-y-0 left-0 w-1" style={{ background: categoryColor(row.profileColor, 500, 'var(--cat-blue-500)') }} />
      {/*
        The identity line: WHO, and WHAT STATE they are in — the two things an
        operator scans a full grid for. They share one row on purpose.

        A 15-minute booking clamps to the 28px floor above, and `px-2 py-1.5`
        leaves ~16px of content box: one `text-xs` line, full stop. The card
        used to stack name-then-(product/time/status), so at that height one of
        the two rows had to lose. Pinning the name (2026-09-14) made the status
        the casualty instead of the patient — the badge sits in the lower row,
        so it vanished from every short card, which is most of a busy day.

        Putting the badge beside the name fixes both at once: the name truncates
        (`flex-1 min-w-0`), the badge never shrinks (`shrink-0`), and together
        they fit the one line a short card has. The product and the time are the
        genuinely optional half — they drop out below 2 lines and come back on a
        taller card. Nothing here shrinks when there IS room, so 30/60/90-minute
        cards are unchanged.
      */}
      <div className="flex items-center gap-1 min-w-0 shrink-0">
        <span className="text-xs font-semibold text-text-primary truncate flex-1 min-w-0">
          {row.patientName}
        </span>
        {row.status ? (
          <StatusBadge
            status={row.status as AppointmentStatusType}
            label={row.statusDisplay}
            className="text-xs px-1 py-px shrink-0"
          />
        ) : null}
      </div>
      {/*
        `min-h-0` so this row can resolve to 0px on a short card, and
        `overflow-hidden` so the clip is clean — without it a flex item cannot
        shrink past its own content and the descenders of the line painted over
        the card's bottom edge (measured in Chromium).
      */}
      <div className="flex items-center gap-1 min-w-0 min-h-0 overflow-hidden">
        <span className="text-xs text-text-muted truncate flex-1">
          {row.profileName ? `${row.profileName} · ${row.timeRange}` : row.timeRange}
        </span>
      </div>
    </button>
  );
}

function defaultHours(): number[] {
  return Array.from({ length: 10 }, (_, i) => i + 8); // 8..17
}
